import type { Rect } from './types';

/**
 * Rotation-aware rectangle math for the carousel's layers.
 *
 * All of it lives here as pure functions rather than inside the drag handlers:
 * sign errors in a rotated frame are subtle and infuriating to chase through a
 * component, and round-trip tests catch them in milliseconds.
 */

export type Handle = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w';

export interface Vec {
  x: number;
  y: number;
}

const rad = (deg: number) => (deg * Math.PI) / 180;

export function rotateVec(v: Vec, deg: number): Vec {
  const c = Math.cos(rad(deg));
  const s = Math.sin(rad(deg));
  return { x: v.x * c - v.y * s, y: v.x * s + v.y * c };
}

export function rotateAround(p: Vec, centre: Vec, deg: number): Vec {
  const r = rotateVec({ x: p.x - centre.x, y: p.y - centre.y }, deg);
  return { x: centre.x + r.x, y: centre.y + r.y };
}

export function rectCentre(r: Rect): Vec {
  return { x: r.x + r.w / 2, y: r.y + r.h / 2 };
}

/** Axis-aligned bounding box of a rotated rectangle. */
export function boundingBox(r: Rect, rotation: number): Rect {
  if (!rotation) return r;
  const c = rectCentre(r);
  const corners = [
    { x: r.x, y: r.y },
    { x: r.x + r.w, y: r.y },
    { x: r.x + r.w, y: r.y + r.h },
    { x: r.x, y: r.y + r.h },
  ].map((p) => rotateAround(p, c, rotation));
  const xs = corners.map((p) => p.x);
  const ys = corners.map((p) => p.y);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  return { x: minX, y: minY, w: Math.max(...xs) - minX, h: Math.max(...ys) - minY };
}

const MIN_SIZE = 8;

export interface ResizeOptions {
  /** keep the rect's proportions (Shift) */
  lockAspect?: boolean;
  /** grow about the centre instead of the opposite edge (Alt) */
  fromCentre?: boolean;
}

/**
 * Apply a screen-space drag to one resize handle of a possibly rotated rect.
 *
 * The delta is rotated into the rect's own frame first, the edges move there,
 * and the resulting centre is rotated back — which is what makes dragging the
 * north-west handle of a 30° layer feel like dragging its own corner rather
 * than some world-axis approximation.
 */
export function resizeInLocalFrame(
  rect: Rect,
  rotation: number,
  handle: Handle,
  delta: Vec,
  opts: ResizeOptions = {},
): Rect {
  const local = rotateVec(delta, -rotation);
  const west = handle.includes('w');
  const east = handle.includes('e');
  const north = handle.startsWith('n');
  const south = handle.startsWith('s');

  let dw = (east ? local.x : 0) - (west ? local.x : 0);
  let dh = (south ? local.y : 0) - (north ? local.y : 0);
  if (opts.fromCentre) {
    dw *= 2;
    dh *= 2;
  }

  let w = Math.max(MIN_SIZE, rect.w + dw);
  let h = Math.max(MIN_SIZE, rect.h + dh);

  if (opts.lockAspect && rect.w > 0 && rect.h > 0) {
    const ratio = rect.w / rect.h;
    // Follow whichever axis the pointer moved further along, so a corner drag
    // never fights the direction of the gesture.
    if (Math.abs(dw) * rect.h > Math.abs(dh) * rect.w) h = Math.max(MIN_SIZE, w / ratio);
    else w = Math.max(MIN_SIZE, h * ratio);
  }

  // Where the box's centre lands, expressed in the rect's own frame.
  const shift = { x: 0, y: 0 };
  if (!opts.fromCentre) {
    if (east) shift.x = (w - rect.w) / 2;
    if (west) shift.x = -(w - rect.w) / 2;
    if (south) shift.y = (h - rect.h) / 2;
    if (north) shift.y = -(h - rect.h) / 2;
  }

  const world = rotateVec(shift, rotation);
  const c = rectCentre(rect);
  return { x: c.x + world.x - w / 2, y: c.y + world.y - h / 2, w, h };
}

/** Angle in degrees from a rect's centre to a point, with 0 straight up. */
export function angleTo(centre: Vec, point: Vec): number {
  const deg = (Math.atan2(point.y - centre.y, point.x - centre.x) * 180) / Math.PI;
  return (deg + 90 + 360) % 360;
}

export function snapAngle(deg: number, step = 15): number {
  return ((Math.round(deg / step) * step) % 360 + 360) % 360;
}
